import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Bot, ArrowLeft, Trash2, GripVertical, Sparkles, RefreshCw, ExternalLink, Loader2, Plus, ChevronDown } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CreditsBadge } from '@/components/CreditsBadge';
import { CreditsDialog } from '@/components/CreditsDialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAISettings } from '@/hooks/useAISettings';
import { useSkills } from '@/hooks/useSkills';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNavigate, Link } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import type { AIProvider } from '@/contexts/AISettingsContext';
import { AI_PROVIDER_PRESETS, type PresetProvider } from '@/lib/aiProviderPresets';
import { ExternalFavicon } from '@/components/ExternalFavicon';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ProjectTemplates } from '@/components/ProjectTemplates';

/** A few provider-specific icon hacks */
function getProviderIconUrl(provider: { id: string; baseURL: string }): string {
  if (provider.id === 'ollama' && provider.baseURL === 'http://localhost:11434/v1') {
    return 'https://ollama.com/';
  } else if (provider.baseURL === 'https://generativelanguage.googleapis.com/v1beta/openai') {
    return 'https://gemini.google.com/';
  } else if (provider.baseURL === 'https://api.z.ai/api/paas/v4') {
    return 'https://docs.z.ai/';
  }
  try {
    const urlObj = new URL(provider.baseURL);
    urlObj.hostname = urlObj.hostname.replace(/^(ai\.|api\.)/, '');
    return urlObj.toString();
  } catch {
    return provider.baseURL;
  }
}

interface SortableProviderItemProps {
  provider: AIProvider;
  preset?: PresetProvider;
  onRemove: (id: string) => void;
  onSetProvider: (provider: AIProvider) => void;
  onOpenCreditsDialog: (providerId: string) => void;
  showDragHandle: boolean;
}

function SortableProviderItem({ provider, preset, onRemove, onSetProvider, onOpenCreditsDialog, showDragHandle }: SortableProviderItemProps) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: provider.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <AccordionItem
      ref={setNodeRef}
      style={style}
      value={provider.id}
      className="border rounded-lg bg-card"
    >
      <AccordionTrigger className="px-4 py-3 hover:no-underline">
        <div className="flex items-center gap-3 w-full mr-3">
          {showDragHandle && (
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-muted-foreground hover:text-foreground"
            >
              <GripVertical className="h-4 w-4" />
            </div>
          )}
          {provider.baseURL ? (
            <ExternalFavicon
              url={getProviderIconUrl(provider)}
              size={16}
              fallback={<Bot size={16} className="text-muted-foreground" />}
            />
          ) : (
            <Bot size={16} className="text-muted-foreground" />
          )}
          <span className="text-sm font-medium">
            {preset?.name || provider.id}
          </span>
          <div className="ml-auto">
            <CreditsBadge
              provider={provider}
              onOpenDialog={() => onOpenCreditsDialog(provider.id)}
            />
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={`${provider.id}-baseURL`} className="text-sm">{t('baseUrl')}</Label>
            <Input
              id={`${provider.id}-baseURL`}
              placeholder="https://api.example.com/v1"
              value={provider.baseURL || ''}
              onChange={(e) => onSetProvider({ ...provider, baseURL: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${provider.id}-auth`} className="text-sm">{t('authentication')}</Label>
            <Select
              value={provider.nostr ? 'nostr' : 'api-key'}
              onValueChange={(value: 'api-key' | 'nostr') => onSetProvider({
                ...provider,
                nostr: value === 'nostr' || undefined,
                apiKey: value === 'nostr' ? undefined : provider.apiKey
              })}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="api-key">{t('apiKey')}</SelectItem>
                <SelectItem value="nostr">Nostr</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!provider.nostr && (
            <div className="space-y-2">
              <Label htmlFor={`${provider.id}-apiKey`} className="text-sm">{t('apiKey')}</Label>
              <PasswordInput
                id={`${provider.id}-apiKey`}
                placeholder={t('enterApiKey')}
                value={provider.apiKey || ''}
                onChange={(e) => onSetProvider({ ...provider, apiKey: e.target.value })}
                className="h-9"
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${provider.id}-proxy`}
              checked={provider.proxy || false}
              onCheckedChange={(checked) => onSetProvider({
                ...provider,
                proxy: checked === true || undefined
              })}
            />
            <Label htmlFor={`${provider.id}-proxy`} className="text-sm cursor-pointer">
              {t('useCorsProxy')}
            </Label>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(provider.id)}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('delete')}
          </Button>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export function AISettings() {
  const { t } = useTranslation();
  const { settings, setProvider, removeProvider, setProviders } = useAISettings();
  const { settings: skillsSettings, addPlugin, removePlugin, togglePlugin, refreshPlugin, isLoading: isLoadingSkills } = useSkills();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const [customProviderName, setCustomProviderName] = useState('');
  const [customBaseURL, setCustomBaseURL] = useState('');
  const [customApiKey, setCustomApiKey] = useState('');
  const [customAuthMethod, setCustomAuthMethod] = useState<'api-key' | 'nostr'>('api-key');
  const [customProxy, setCustomProxy] = useState(false);
  const [presetApiKeys, setPresetApiKeys] = useState<Record<string, string>>({});
  const [presetTermsAgreements, setPresetTermsAgreements] = useState<Record<string, boolean>>({});
  const [activeCreditsDialog, setActiveCreditsDialog] = useState<string | null>(null);
  const [pluginUrl, setPluginUrl] = useState('');
  const [isAddingPlugin, setIsAddingPlugin] = useState(false);

  const handleAddPlugin = async () => {
    if (!pluginUrl.trim()) return;

    setIsAddingPlugin(true);
    try {
      await addPlugin(pluginUrl.trim());
      setPluginUrl('');
      toast({
        title: t('pluginAddedSuccessfully'),
        description: t('pluginAddedDescription'),
      });
    } catch (error) {
      toast({
        title: t('failedToAddPlugin'),
        description: error instanceof Error ? error.message : t('unknownError'),
        variant: 'destructive',
      });
    } finally {
      setIsAddingPlugin(false);
    }
  };

  const handleRemovePlugin = (id: string) => {
    removePlugin(id);
    toast({
      title: t('pluginRemoved'),
      description: t('pluginRemovedDescription'),
    });
  };

  const handleRefreshPlugin = async (id: string) => {
    try {
      await refreshPlugin(id);
      toast({
        title: t('pluginRefreshed'),
        description: t('pluginRefreshedDescription'),
      });
    } catch (error) {
      toast({
        title: t('failedToRefreshPlugin'),
        description: error instanceof Error ? error.message : t('unknownError'),
        variant: 'destructive',
      });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAddPresetProvider = (preset: PresetProvider) => {
    const apiKey = presetApiKeys[preset.id] as string | undefined;
    if (!presetTermsAgreements[preset.id]) return;

    const newProvider: AIProvider = {
      id: preset.id,
      baseURL: preset.baseURL,
    };

    if (typeof apiKey === 'string') {
      newProvider.apiKey = apiKey.trim();
    }
    if (typeof preset.nostr === 'boolean') {
      newProvider.nostr = preset.nostr;
    }
    if (typeof preset.proxy === 'boolean') {
      newProvider.proxy = preset.proxy;
    }

    setProvider(newProvider);
    setPresetApiKeys(prev => ({ ...prev, [preset.id]: '' }));
    setPresetTermsAgreements(prev => ({ ...prev, [preset.id]: false }));
  };

  const handleAddCustomProvider = () => {
    if (!customProviderName.trim() || !customBaseURL.trim()) return;

    const newProvider: AIProvider = {
      id: customProviderName.trim(),
      baseURL: customBaseURL.trim(),
      apiKey: customAuthMethod === 'nostr' ? undefined : customApiKey.trim(),
      nostr: customAuthMethod === 'nostr' || undefined,
      proxy: customProxy || undefined,
    };

    setProvider(newProvider);
    setCustomProviderName('');
    setCustomBaseURL('');
    setCustomApiKey('');
    setCustomAuthMethod('api-key');
    setCustomProxy(false);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = settings.providers.findIndex(provider => provider.id === active.id);
      const newIndex = settings.providers.findIndex(provider => provider.id === over?.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newProviders = arrayMove(settings.providers, oldIndex, newIndex);
        setProviders(newProviders);
      }
    }
  };

  const configuredProviderIds = settings.providers.map(p => p.id);
  const availablePresets = AI_PROVIDER_PRESETS.filter(preset => !configuredProviderIds.includes(preset.id));

  return (
    <div className="p-6 space-y-8">
      {isMobile && (
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/settings')}
            className="h-8 px-2 -ml-2 text-muted-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('settings')}
          </Button>
        </div>
      )}

      <div>
        <h1 className="text-xl font-semibold tracking-tight">{t('aiSettings')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('aiSettingsDescriptionLong')}
        </p>
      </div>

      <div className="space-y-6">
        {/* Configured Providers */}
        {settings.providers.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t('configuredProviders')}</h3>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={settings.providers.map(p => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <Accordion type="multiple" className="space-y-2">
                  {settings.providers.map((provider) => {
                    const preset = AI_PROVIDER_PRESETS.find(p => p.id === provider.id);
                    return (
                      <SortableProviderItem
                        key={provider.id}
                        provider={provider}
                        preset={preset}
                        onRemove={removeProvider}
                        onSetProvider={setProvider}
                        onOpenCreditsDialog={setActiveCreditsDialog}
                        showDragHandle={settings.providers.length > 1}
                      />
                    );
                  })}
                </Accordion>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* Available Preset Providers */}
        {availablePresets.length > 0 && (
          <div className="space-y-3">
            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:text-foreground/80 transition-colors group w-full">
                <Plus className="h-4 w-4 text-muted-foreground" />
                <span>{t('addProvider')}</span>
                <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180 ml-auto" />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                <div className="grid gap-3">
                  {availablePresets.map((preset) => {
                const isNostrPreset = preset.nostr;
                const isLoggedIntoNostr = !!user;
                const showNostrLoginRequired = isNostrPreset && !isLoggedIntoNostr;

                return (
                  <div key={preset.id} className="border rounded-lg p-4 bg-card space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ExternalFavicon
                          url={getProviderIconUrl(preset)}
                          size={16}
                          fallback={<Bot size={16} className="text-muted-foreground" />}
                        />
                        <span className="text-sm font-medium">{preset.name}</span>
                      </div>
                      {(preset.apiKeysURL && preset.id !== "routstr") && (
                        <button
                          type="button"
                          className="text-xs text-muted-foreground hover:text-foreground underline"
                          onClick={() => window.open(preset.apiKeysURL, '_blank')}
                        >
                          {t('getApiKey')}
                        </button>
                      )}
                    </div>

                    {showNostrLoginRequired ? (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          {t('loginToNostrRequired')}
                        </p>
                        <Button asChild size="sm" className="w-full">
                          <Link to="/settings/nostr">{t('goToNostrSettings')}</Link>
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          {!preset.nostr && (
                            <PasswordInput
                              placeholder={preset.id === "routstr" ? t('enterCashuToken') : t('enterApiKey')}
                              className="flex-1 h-9"
                              value={presetApiKeys[preset.id] || ''}
                              onChange={(e) => setPresetApiKeys(prev => ({ ...prev, [preset.id]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && presetApiKeys[preset.id]?.trim() && presetTermsAgreements[preset.id]) {
                                  handleAddPresetProvider(preset);
                                }
                              }}
                            />
                          )}
                          <Button
                            onClick={() => handleAddPresetProvider(preset)}
                            disabled={!presetTermsAgreements[preset.id] || (!!preset.apiKeysURL && !presetApiKeys[preset.id]?.trim())}
                            size="sm"
                            className={preset.nostr ? "w-full h-9" : "h-9 px-4"}
                          >
                            {t('add')}
                          </Button>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Checkbox
                            id={`agree-terms-${preset.id}`}
                            checked={presetTermsAgreements[preset.id] || false}
                            onCheckedChange={(checked) => setPresetTermsAgreements(prev => ({ ...prev, [preset.id]: checked === true }))}
                            className="h-3 w-3"
                          />
                          <label
                            htmlFor={`agree-terms-${preset.id}`}
                            className="text-xs text-muted-foreground cursor-pointer"
                          >
                            {t('agreeToTermsOfService', { providerName: preset.name })}{' '}
                            {preset.tosURL ? (
                              <a
                                href={preset.tosURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline hover:text-foreground"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {t('termsOfService')}
                              </a>
                            ) : (
                              t('termsOfService')
                            )}
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {/* Custom Provider */}
        <div className="space-y-3">
          <Accordion type="single" collapsible>
            <AccordionItem value="custom-provider" className="border rounded-lg bg-card">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center gap-3">
                  <Plus className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{t('addCustomProvider')}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="custom-name" className="text-sm">{t('providerName')}</Label>
                    <Input
                      id="custom-name"
                      placeholder="e.g., my-custom-api"
                      value={customProviderName}
                      onChange={(e) => setCustomProviderName(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="custom-baseurl" className="text-sm">{t('baseUrl')}</Label>
                    <Input
                      id="custom-baseurl"
                      placeholder="https://api.example.com/v1"
                      value={customBaseURL}
                      onChange={(e) => setCustomBaseURL(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="custom-auth" className="text-sm">{t('authentication')}</Label>
                    <Select
                      value={customAuthMethod}
                      onValueChange={(value: 'api-key' | 'nostr') => {
                        setCustomAuthMethod(value);
                        if (value === 'nostr') setCustomApiKey('');
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="api-key">{t('apiKey')}</SelectItem>
                        <SelectItem value="nostr">Nostr</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {customAuthMethod === 'api-key' && (
                    <div className="space-y-2">
                      <Label htmlFor="custom-apikey" className="text-sm">{t('apiKey')}</Label>
                      <PasswordInput
                        id="custom-apikey"
                        placeholder={t('enterApiKey') + ' (optional)'}
                        value={customApiKey}
                        onChange={(e) => setCustomApiKey(e.target.value)}
                        className="h-9"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="custom-proxy"
                      checked={customProxy}
                      onCheckedChange={(checked) => setCustomProxy(checked === true)}
                    />
                    <Label htmlFor="custom-proxy" className="text-sm cursor-pointer">
                      {t('useCorsProxy')}
                    </Label>
                  </div>
                  <Button
                    onClick={handleAddCustomProvider}
                    disabled={!customProviderName.trim() || !customBaseURL.trim() || settings.providers.some(p => p.id === customProviderName.trim())}
                    size="sm"
                    className="gap-2"
                  >
                    <Check className="h-4 w-4" />
                    {t('addCustomProviderButton')}
                  </Button>
                  {settings.providers.some(p => p.id === customProviderName.trim()) && (
                    <p className="text-sm text-destructive">{t('providerExists')}</p>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Plugins Section */}
        <div className="space-y-3 pt-4 border-t">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            <h3 className="text-sm font-medium">{t('plugins')}</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('pluginsDescription')}
          </p>

          {/* Add Plugin */}
          <div className="flex gap-2">
            <Input
              placeholder={t('pluginUrlPlaceholder')}
              value={pluginUrl}
              onChange={(e) => setPluginUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && pluginUrl.trim() && !isAddingPlugin) {
                  handleAddPlugin();
                }
              }}
              className="h-9 flex-1"
            />
            <Button
              onClick={handleAddPlugin}
              disabled={!pluginUrl.trim() || isAddingPlugin}
              size="sm"
              className="h-9"
            >
              {isAddingPlugin ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('add')
              )}
            </Button>
          </div>

          {/* Configured Plugins */}
          {skillsSettings.plugins.length > 0 && (
            <Accordion type="multiple" className="space-y-2">
              {skillsSettings.plugins.map((plugin) => (
                <AccordionItem
                  key={plugin.id}
                  value={plugin.id}
                  className="border rounded-lg bg-card"
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline">
                    <div className="flex items-center justify-between w-full mr-3">
                      <div className="flex items-center gap-3">
                        {plugin.isBuiltIn ? (
                          <Sparkles size={16} className="text-primary" />
                        ) : (
                          <ExternalFavicon
                            url={plugin.url}
                            size={16}
                            fallback={<Sparkles size={16} className="text-muted-foreground" />}
                          />
                        )}
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{plugin.name}</span>
                          <Badge variant="secondary" className="text-xs">
                            {plugin.skills.length} {plugin.skills.length === 1 ? t('skill') : t('skills')}
                          </Badge>
                          {plugin.isBuiltIn && (
                            <Badge variant="outline" className="text-xs">
                              Built-in
                            </Badge>
                          )}
                          {plugin.isDefault && !plugin.isBuiltIn && (
                            <Badge variant="outline" className="text-xs">
                              {t('default')}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={plugin.enabled}
                          onCheckedChange={() => togglePlugin(plugin.id)}
                        />
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4">
                      {/* Skills List */}
                      {plugin.skills.length > 0 && (
                        <div className="space-y-1.5">
                          {plugin.skills.map((skill) => (
                            <div
                              key={skill.name}
                              className="text-xs text-muted-foreground flex items-start gap-2 pl-1"
                            >
                              <span className="text-primary">•</span>
                              <div>
                                <span className="font-medium text-foreground">{skill.name}</span>
                                <span className="ml-1">— {skill.description}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-3 border-t">
                        {plugin.url ? (
                          <a
                            href={plugin.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {t('viewSource')}
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            Built-in skill
                          </span>
                        )}
                        <div className="flex items-center gap-2">
                          {!plugin.isBuiltIn && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRefreshPlugin(plugin.id)}
                              disabled={isLoadingSkills}
                              className="h-7 px-2 text-muted-foreground hover:text-foreground"
                            >
                              <RefreshCw className={`h-3 w-3 ${isLoadingSkills ? 'animate-spin' : ''}`} />
                            </Button>
                          )}
                          {!plugin.isDefault && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemovePlugin(plugin.id)}
                              className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}

          {skillsSettings.plugins.length === 0 && (
            <div className="text-center py-6 border border-dashed rounded-lg">
              <Sparkles className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                {t('noPluginsConfigured')}
              </p>
              <a
                href="https://github.com/anthropics/skills"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline mt-1 inline-block"
              >
                {t('browseSkills')}
              </a>
            </div>
          )}
        </div>

        {/* Project Templates Section */}
        <div className="pt-4 border-t">
          <ProjectTemplates />
        </div>

        {/* Advanced Section */}
        <Collapsible className="pt-4 border-t">
          <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:text-foreground/80 transition-colors group w-full">
            <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
            {t('advanced')}
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-4">
            {/* Advanced settings can go here in the future */}
            <p className="text-sm text-muted-foreground">No advanced settings yet.</p>
          </CollapsibleContent>
        </Collapsible>
      </div>

      {activeCreditsDialog && (() => {
        const provider = settings.providers.find(p => p.id === activeCreditsDialog);
        return provider && (
          <CreditsDialog
            open={true}
            onOpenChange={(open) => { if (!open) setActiveCreditsDialog(null); }}
            provider={provider}
          />
        );
      })()}
    </div>
  );
}

export default AISettings;
